import oc from "open-color";
import React, { ReactElement, useState } from "react";
import { TbReportAnalytics } from "react-icons/tb";
import t from "../helpers/i18n";
import CustomModal from "./CustomModal";

export interface Report {
  name: string;
  link: string;
}

const LINK_COLOR = "#17a2b8";
const LINK_FONT_SIZE = "13px";

export default function ReportsDropdown({
  reports,
  saveReportsHandler,
  generalReportParamsIds,
}: {
  reports: Report[];
  saveReportsHandler: (reports: Report[]) => Promise<void>;
  generalReportParamsIds?: number[];
}): ReactElement {
  const [modalIsOpen, setModalOpen] = useState(false);
  const openModal = (): void => setModalOpen(true);
  const closeModal = (): void => setModalOpen(false);

  return (
    <div className="dropdown position-relative">
      <button
        className="btn btn-info dropdown-toggle rounded-sm"
        type="button"
        data-toggle="dropdown"
        aria-expanded="false"
      >
        <TbReportAnalytics
          size={16}
          style={{ marginRight: "5px", position: "relative", top: "-2px" }}
        />
        {t("Saved reports", "Предналаштовані звіти", "Сохранённые отчёты", "Zapisane raporty")}
      </button>

      <div className="dropdown-menu dropdown-menu-right" style={{ padding: 0 }}>
        {!!generalReportParamsIds?.length && (
          <a
            className="dropdown-item"
            style={{ padding: "7px", fontSize: LINK_FONT_SIZE, color: LINK_COLOR }}
            href={`/manage/reports/readings/?paramIds=${generalReportParamsIds.join() || ""}`}
            target="_blank"
          >
            {t("General report", "Загальний звіт", "Общий отчёт", "Raport ogólny")}
          </a>
        )}
        {reports.map((report, i) => (
          <div key={i} className="d-flex">
            <a
              className="dropdown-item"
              href={report.link}
              target="_blank"
              style={{
                fontSize: LINK_FONT_SIZE,
                padding: "7px",
                textOverflow: "ellipsis",
                width: "240px",
                overflow: "hidden",
                whiteSpace: "nowrap",
                color: LINK_COLOR,
              }}
            >
              {report.name}
            </a>
            <button
              type="button"
              style={{
                color: oc.red[7],
                border: "none",
                backgroundColor: "transparent",
                padding: "0px 10px",
              }}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={async () => {
                if (
                  !confirm(
                    t(
                      `Confirm deleting report "${report.name}"?`,
                      `Ви впевнені, що хочете видалити звіт "${report.name}"?`,
                      `Вы уверены, что хотите удалить отчёт "${report.name}"?`,
                      `Potwierdzić usunięcie raportu "${report.name}"?`,
                    ),
                  )
                ) {
                  return;
                }
                await saveReportsHandler(reports.filter((_, index) => index !== i));
              }}
            >
              <i className="fa fa-trash"></i>
            </button>
          </div>
        ))}
        <div className="dropdown-divider" style={{ padding: 0, margin: 0 }}></div>
        <button
          type="button"
          style={{
            padding: "7px",
            color: LINK_COLOR,
            fontSize: LINK_FONT_SIZE,
            border: "none",
            backgroundColor: "transparent",
          }}
          onClick={openModal}
        >
          {t("Add report...", "Додати звіт...", "Добавить отчёт...", "Dodać raport...")}
        </button>
        <CustomModal
          isOpen={modalIsOpen}
          title={t(
            "Add report link",
            "Додати посилання на звіт",
            "Добавить ссылку на отчёт",
            "Dodać link do raportu",
          )}
          closeModal={closeModal}
        >
          <AddReportForm
            saveReportsHandler={saveReportsHandler}
            reports={reports}
            closeModal={closeModal}
          />
        </CustomModal>
      </div>
    </div>
  );
}

function AddReportForm({
  saveReportsHandler,
  reports,
  closeModal,
}: {
  saveReportsHandler: (reports: Report[]) => Promise<void>;
  reports: Report[];
  closeModal: () => void;
}): ReactElement {
  const [formData, setFormData] = useState<Report>({ name: "", link: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <form>
      <div className="form-group text-left">
        <label htmlFor="report-name" className="required">
          {t("Name:", "Назва:")}
        </label>
        <input
          type="text"
          id="report-name"
          className="form-control"
          value={formData.name}
          required
          onChange={(e) => {
            setFormData({ ...formData, name: e.target.value });
          }}
        />
      </div>
      <div className="form-group text-left">
        <label htmlFor="report-link" className="required">
          {t("Link", "Посилання")}:
        </label>
        <input
          type="text"
          id="report-link"
          className="form-control"
          value={formData.link}
          required
          onChange={(e) => {
            setFormData({ ...formData, link: e.target.value });
          }}
        />
      </div>
      <div style={{ textAlign: "left" }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={isSubmitting}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={async () => {
            setIsSubmitting(true);
            await saveReportsHandler([...reports, formData]);
            setIsSubmitting(false);
            closeModal();
            setFormData({ name: "", link: "" });
          }}
        >
          {t("Add", "Додати", "Добавить", "Dodać")}
        </button>
      </div>
    </form>
  );
}
